import { memo } from 'react';

import { type PublicBoard } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { BoardCover } from '~/components/v2/components/board/board-cover/BoardCover';
import { getBoardCoverCornerClass } from '~/components/v2/components/board/board-cover/boardCoverCornerClass';

export type PublicBoardCoverProps = {
  board: PublicBoard;
};

export const PublicBoardCover = memo(({ board }: PublicBoardCoverProps) => {
  // Same context source the page chrome's corner classes derive from. `undefined` for
  // standard/absent, so a legacy link's cover class list stays byte-identical to today (the cover
  // keeps BoardCover's native rounded-8).
  const { presentationSettings } = useShortIdContext();
  const coverCornerClass = getBoardCoverCornerClass(presentationSettings?.thumbnailRadius);

  // For public boards, we need to check if the cover image actually belongs to this board
  // The API returns inherited cover images for subboards, so we need to detect if it's inherited
  const hasAncestors = board.ancestors && board.ancestors.length > 0;

  // If no cover image, don't show anything
  if (!board.coverImage) {
    return null;
  }

  // For top-level boards (no ancestors), always show the cover
  if (!hasAncestors) {
    return (
      <div className="px-4">
        <BoardCover className={cn('aspect-[5.5/1] max-h-[280px]', coverCornerClass)} board={board} />
      </div>
    );
  }

  // For subboards, check if the cover image's clipId belongs to this board's assets
  // This is a more accurate way to detect if the cover is owned vs inherited
  const coverClipId = board.coverImage?.clipId;

  // If we have a cover image with a clipId, check if it belongs to this board
  if (coverClipId) {
    // Check if the clipId is in the board's thumbnails (which represent the board's assets)
    // thumbnails are URLs, so we need to extract the clipId from them
    const thumbnailClipIds = board.thumbnails
      ?.map((thumbnail) => {
        // Extract clipId from URL like 'https://air-dev.imgix.net/2e158187-fff6-45b2-97da-8cd0f4c158aa.jpg'
        const match = thumbnail.match(/\/([a-f0-9-]+)\./);

        return match ? match[1] : null;
      })
      .filter(Boolean);

    const hasOwnCover = thumbnailClipIds?.includes(coverClipId);

    if (!hasOwnCover) {
      // This cover image doesn't belong to this board, so it's inherited
      return null;
    }
  }

  return (
    <div className="px-4">
      <BoardCover className={coverCornerClass} board={board} />
    </div>
  );
});

PublicBoardCover.displayName = 'PublicBoardCover';
